import { InputRef } from 'antd';
import { ChangeEvent, forwardRef, useCallback } from 'react';
import { PHONE_MAX_LENGTH } from '@/constants/regex';
import TextInput, { TInputProps } from './Input';

// Digits only, plus an optional leading '+' for the country code. The length is
// capped here rather than through the input's own `maxLength`, which would cut
// a pasted `+47 12 34 56 78` down to size before the spaces come out and leave
// a silently truncated number that still passes validation.
const sanitizePhone = (value: string) => {
  const digitsOnly = value.replace(/\D/g, '');
  const withCountryCode = value.startsWith('+') ? `+${digitsOnly}` : digitsOnly;

  return withCountryCode.slice(0, PHONE_MAX_LENGTH);
};

const PhoneInput = forwardRef<InputRef, TInputProps>((props, ref) => {
  const { onChange, ...restProps } = props;

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const sanitized = sanitizePhone(e.target.value);

      if (sanitized === e.target.value) {
        onChange?.(e);
        return;
      }

      onChange?.({
        ...e,
        target: { ...e.target, value: sanitized },
        currentTarget: { ...e.currentTarget, value: sanitized },
      } as ChangeEvent<HTMLInputElement>);
    },
    [onChange],
  );

  return <TextInput {...restProps} ref={ref} type="tel" inputMode="tel" onChange={handleChange} />;
});

PhoneInput.displayName = 'PhoneInput';

export default PhoneInput;
